/**
 * components/Money
 * ──────────────────────────────────────────────────────────────────────────
 * Every amount in the product renders through this component. Doing it in one
 * place is what guarantees three things the design requires and that are easy
 * to lose one screen at a time:
 *
 *   1. IBM Plex Mono with tabular figures, so a column of amounts lines up.
 *   2. `direction: ltr`, so "AED 340.00" is never re-ordered by an RTL locale.
 *      An amount is a quantity, not a sentence.
 *   3. Never wrapping. A balance broken across two lines is a different number
 *      at a glance.
 *
 * `signed` renders the ledger's "+ INR 12,400.00" / "− AED 80.00" form, where
 * direction comes from the transaction TYPE — never from a negative amount, as
 * amounts are stored positive.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { Text, type TextStyle } from 'react-native';
import { neutral } from '../../theme/colors';
import { mono, numeric, size as fontSize } from '../../theme/typography';
import { format, formatSigned } from '../../lib/money';

export interface MoneyProps {
  /** Minor units, as the API sends them. */
  value: string | number | null | undefined;
  cur: string;
  /** Ledger form with an explicit direction glyph. */
  direction?: 'in' | 'out';
  size?: number;
  weight?: '400' | '500' | '600';
  color?: string;
  /** Right-align inside a table cell. */
  align?: 'left' | 'right';
  style?: TextStyle;
}

export function Money({
  value,
  cur,
  direction,
  size = fontSize.body,
  weight = '500',
  color = neutral.ink,
  align = 'left',
  style,
}: MoneyProps) {
  const text = direction ? formatSigned(value, cur, direction) : format(value, cur);

  return (
    <Text
      numberOfLines={1}
      style={[
        mono(weight),
        numeric,
        { fontSize: size, color, textAlign: align },
        style,
      ]}
    >
      {text}
    </Text>
  );
}

export default Money;
